/*
 * render.js
 *
 * Owns the top-level UI: header, tab bar, panel container. Every
 * mutation in actions.js calls `render()` to repaint, exactly like
 * the original artifact's behaviour. `switchTab()` is a focused
 * variant that only repaints the active panel.
 */

import { state } from './state.js';
import { TABS_P, TABS_S, SUBTABS_P, SUBTABS_S } from './constants.js';
import { accent, applyTheme, refreshAutoItems, runAutoActions,
         isKijker, mate, mateMayView, nameFor } from './helpers.js';
import { drawAllPies } from './pie.js';
import { resetOpenRow } from './swipe.js';
import { icon } from './icons.js';
import { supabase } from './lib/supabase.js';
import {
  buildHome,
  buildItemList,
  buildExtra,
  buildDromen,
  buildSamenOverzicht,
  buildUitgaven,
  buildAddModal,
  buildInkomsten,
  buildSettingsModal,
  buildBegrootModal,
  buildBespaarModal,
  buildReflectModal,
  buildAbsenceModal,
  buildAutoNoticeModal,
  buildSpaarSkipModal,
  buildAddItemModal,
  buildMeldingen,
  notifBadgeCount,
  notifHasRed,
  buildFabPickerModal
} from './panels.js';
import { readFabSide } from './lib/fab-pref.js';

/** Switch the active person. Called from the header tabs. */
export function setPerson(p, btn) {
  state.person = p;
  state.editing = {};
  state.pendingAdd = null;

  document.querySelectorAll('.psw').forEach(b => b.classList.remove('active'));
  btn.classList.add('active');

  // Different default tab depending on which view we're in.
  state.activeTab = p === 'samen' ? 'overzicht' : 'home';
  // Sub-tab default afhankelijk van persoon-modus zodat een latere klik op
  // Uitgaven meteen op een geldige sub valt (samen kent geen 'extra').
  state.activeSubTab = p === 'samen' ? 'svast' : 'extra';
  render();
}

/**
 * Full re-render: rebuilds the header chrome (tab bar) and all
 * panels' shells, then draws the active panel via the same dispatch
 * `switchTab()` uses. Cheap at this scale.
 */
export function render() {
  // Thema (pride op IDAHOT/juni) bij elke render herevalueren — anders
  // mist een datum-overgang of een instellings-wijziging zonder reload.
  applyTheme();

  // Auto-items (Gezamenlijke kosten) bij elke render vers berekenen
  // zodat wijzigingen in Samen meteen doorrekenen bij de personen.
  refreshAutoItems('sven');
  refreshAutoItems('lander');
  // Auto-distribute sparen/gem voor nieuwe cycli (idempotent per cyclus).
  runAutoActions('sven');
  runAutoActions('lander');
  resetOpenRow();

  const tabs = state.person === 'samen' ? TABS_S : TABS_P;
  const ac = accent();
  const kijker = isKijker();

  // Mount the app shell if it isn't already present. The first call
  // happens once at startup; subsequent calls just refresh contents.
  ensureShell();

  // .kijker-mode class op de app schakelt CSS-regels die write-controls verbergen.
  document.getElementById('app').classList.toggle('kijker-mode', kijker);
  // .notifs-mode klasse: verbergt tab-bar, person-dropdown blokkeert, etc.
  document.getElementById('app').classList.toggle('notifs-mode', state.notifsOpen);

  // FAB-positie volgt de linkshandig-pref (per-device, localStorage).
  const fab = document.getElementById('fab');
  if (fab) fab.classList.toggle('fab-left', readFabSide() === 'left');

  // Persoon-switcher elke render herbouwen — toont mate-knop alleen als mate
  // de share-toggle aan heeft staan.
  document.querySelector('.psw-row').innerHTML = buildPersonSwitcher();

  // In meldingen-modus: tab-bar leeg houden, paneel = volledig meldingen-scherm.
  if (state.notifsOpen) {
    document.getElementById('tabbar').innerHTML = '';
    document.getElementById('bottomnav').innerHTML = '';
    document.getElementById('panels').innerHTML = `
      <div class="panel active" id="p-meldingen">
        ${buildMeldingen()}
      </div>`;
  } else {
    // De .tabs-bar wordt op desktop als sidebar getoond (zie media query in
    // app.css). Op mobile is hij verborgen — daar gebruiken we de bottom-nav.
    // Beide bouwen we elk render-pass; CSS bepaalt zichtbaarheid.
    document.getElementById('tabbar').innerHTML = tabs.map(t => `
      <div class="tab${t.id === state.activeTab ? ' active' : ''}"
           data-tab-id="${t.id}"
           style="${t.id === state.activeTab ? 'color:' + ac : ''}"
           onclick="switchTab('${t.id}', this)">${t.lbl}</div>
    `).join('');

    // Mobile bottom-nav: dezelfde 4 (persoon) / 2 (samen) tabs als icoon-knop.
    // Active = state.activeTab match (Uitgaven blijft hooglicht als je in een
    // van de sub-tabs zit, omdat activeTab dan 'uitgaven' is).
    document.getElementById('bottomnav').innerHTML = tabs.map(t => `
      <button class="bnav-btn${t.id === state.activeTab ? ' active' : ''}"
              data-tab-id="${t.id}"
              onclick="switchTab('${t.id}', this)"
              aria-label="${t.lbl}">
        ${icon(t.icon, '', 22)}
        <span class="bnav-lbl">${t.lbl}</span>
      </button>
    `).join('');

    const kijkerBanner = kijker
      ? `<div class="kijker-banner">👁 Je kijkt mee bij ${nameFor(state.person)} — niets aanpassen mogelijk</div>`
      : '';

    document.getElementById('panels').innerHTML = kijkerBanner + tabs.map(t => `
      <div class="panel${t.id === state.activeTab ? ' active' : ''}" id="p-${t.id}">
        ${buildPanel(t.id)}
      </div>
    `).join('');
  }

  document.getElementById('modal-root').innerHTML =
    buildAddModal() + buildSettingsModal() + buildBegrootModal() + buildBespaarModal() + buildReflectModal() + buildAbsenceModal() + buildAutoNoticeModal() + buildSpaarSkipModal() + buildAddItemModal() + buildFabPickerModal();

  applyInputModes();
  requestAnimationFrame(drawAllPies);
}

/**
 * Activeert het juiste mobiele toetsenbord per `type="number"` input.
 *  - step met decimalen ("0.01", "0.1") → inputmode="decimal" (numeriek + komma).
 *  - integer ranges (max <= 100, of step="1") → inputmode="numeric" (puur cijfers).
 *  - rest → inputmode="decimal" als veilige default (bedragen).
 * Eén pass na elke render — geen veranderingen aan de templates nodig.
 */
function applyInputModes() {
  document.querySelectorAll('input[type="number"]').forEach(inp => {
    if (inp.inputMode) return;          // al gezet
    const step = inp.getAttribute('step') || '';
    const max  = parseInt(inp.getAttribute('max') || '');
    const isDecimalStep = step.includes('.');
    const isIntegerRange = !isDecimalStep && (step === '1' || (Number.isFinite(max) && max <= 100));
    inp.inputMode = isIntegerRange ? 'numeric' : 'decimal';
  });
}

/**
 * Header-acties: accountnaam-dropdown (kies welke Florijn je bekijkt) +
 * meldingen-bel met badge + tandwiel. In meldingen-modus wordt de naam-knop
 * een terug-pijl en zijn bell + gear verborgen — alleen "terug" is dan
 * mogelijk, exact wat de user voorstelde.
 *
 * Klik-vlakken:
 *  - Accountnaam knop → togglePersonMenu (sluit meldingen-modus indien open).
 *  - Bell             → openNotifs.
 *  - Gear             → openSettings.
 *  - Dropdown-item    → pickPerson('sven' | 'lander' | 'samen').
 */
function buildPersonSwitcher() {
  const notifs = state.notifsOpen;
  const labelPerson = notifs ? state.user : state.person;
  const cnt = notifBadgeCount(state.user);
  const hasRed = notifHasRed(state.user);
  const badge = !notifs && cnt > 0
    ? `<span class="psw-bell-badge">${cnt > 99 ? '99+' : cnt}</span>`
    : '';

  const nameBtn = `<button class="psw-name${notifs ? ' psw-name-back' : ''}"
    onclick="togglePersonMenu()"
    title="${notifs ? 'Terug' : 'Wissel Florijn'}">
    ${notifs ? icon('arrow-left', 'color:currentColor', 16) : ''}
    <span class="psw-name-lbl">${nameFor(labelPerson)}</span>
    ${notifs ? '' : icon('chevron-down', 'color:currentColor;margin-left:2px', 14)}
  </button>`;

  const bellBtn = notifs
    ? ''
    : `<button class="psw-icon-btn${hasRed ? ' psw-icon-btn-alert' : ''}" title="Meldingen${hasRed ? ' (urgent)' : ''}" onclick="openNotifs()">
        ${icon('bell', 'color:currentColor', 18)}
        ${badge}
      </button>`;

  const gearBtn = notifs
    ? ''
    : `<button class="psw-icon-btn" title="Instellingen" onclick="openSettings()">
        ${icon('settings', 'color:currentColor', 18)}
      </button>`;

  return nameBtn + bellBtn + gearBtn + buildPersonMenu();
}

/** Dropdown-paneel onder de accountnaam-knop. Verborgen tenzij open. */
function buildPersonMenu() {
  if (!state.personMenuOpen || state.notifsOpen) return '';
  const m = mate();
  const showMate = m && mateMayView(m);

  const row = (id, label, extraCls = '', extraStyle = '') => {
    const active = state.person === id ? ' active' : '';
    return `<button class="psw-menu-item${active} ${extraCls}" style="${extraStyle}" onclick="pickPerson('${id}')">${label}</button>`;
  };

  // Uitloggen-rij staat alleen op als er een Supabase-sessie kan zijn.
  // In dev zonder Supabase config zou klikken een fout geven, dus dan weglaten.
  const signOutRow = supabase
    ? `<button class="psw-menu-item psw-menu-signout" onclick="signOut()">Uitloggen</button>`
    : '';

  // Samen alleen tonen als het huishouden een gezamenlijke rekening heeft
  // (settings → 🏠 Huishouden). Bestaande samen-data blijft staan in
  // state.S.samen — gewoon visueel verborgen tot de toggle weer aan gaat.
  const samenRow = state.hasSharedAccount ? row('samen', 'Samen') : '';

  return `<div class="psw-menu-backdrop" onclick="closePersonMenu()"></div>
    <div class="psw-menu" role="menu">
      ${row(state.user, `${nameFor(state.user)} (eigen)`)}
      ${samenRow}
      ${showMate ? row(m, `👁 ${nameFor(m)}`, 'psw-menu-kijker') : ''}
      ${signOutRow}
    </div>`;
}

/** Switch tabs without rebuilding the whole shell. */
export function switchTab(id, el) {
  refreshAutoItems('sven');
  refreshAutoItems('lander');
  resetOpenRow();

  // Bij klik op Uitgaven (bottom-nav of sidebar) altijd terugvallen op de
  // default sub van de huidige persoon-modus — Lander's expliciete UX-keuze:
  // "klik op uitgaven → opent automatisch extra kosten". Onthouden van de
  // vorige sub zou gebruikers verwarren als ze pas later terugkeren.
  if (id === 'uitgaven') {
    const isSamen = state.person === 'samen';
    state.activeSubTab = isSamen ? 'svast' : 'extra';
  }

  state.activeTab = id;

  // Update bottom-nav highlight (mobile). Top sidebar wordt verderop bijgewerkt.
  document.querySelectorAll('.bnav-btn').forEach(b => {
    b.classList.toggle('active', b.dataset.tabId === id);
  });

  document.querySelectorAll('.tab').forEach(t => {
    t.classList.toggle('active', t.dataset.tabId === id);
    if (t.dataset.tabId === id) t.style.color = accent();
    else t.style.color = '';
  });
  document.querySelectorAll('.panel').forEach(p => p.classList.remove('active'));

  const panel = document.getElementById('p-' + id);
  panel.innerHTML = buildPanel(id);
  panel.classList.add('active');

  applyInputModes();
  requestAnimationFrame(drawAllPies);
}

/**
 * Wissel sub-tab binnen de Uitgaven-tab. Zet activeTab='uitgaven' indien
 * nog niet, dan activeSubTab=id, en herbouwt de uitgaven-panel-content.
 * Wordt aangeroepen door (a) de sub-tab-knoppen binnen het paneel zelf, en
 * (b) inline-onclicks vanuit notifs (vroeger switchTabById('vast')).
 */
export function switchSubTab(id) {
  refreshAutoItems('sven');
  refreshAutoItems('lander');
  resetOpenRow();

  state.activeSubTab = id;

  // Forceer dat we op de uitgaven-tab staan — bv. wanneer een notif van Home
  // direct naar Vaste kosten springt.
  if (state.activeTab !== 'uitgaven') {
    state.activeTab = 'uitgaven';

    // Bottom-nav + sidebar highlight bijwerken.
    document.querySelectorAll('.bnav-btn').forEach(b => {
      b.classList.toggle('active', b.dataset.tabId === 'uitgaven');
    });
    document.querySelectorAll('.tab').forEach(t => {
      t.classList.toggle('active', t.dataset.tabId === 'uitgaven');
      t.style.color = t.dataset.tabId === 'uitgaven' ? accent() : '';
    });
    document.querySelectorAll('.panel').forEach(p => p.classList.remove('active'));

    const panel = document.getElementById('p-uitgaven');
    panel.classList.add('active');
  }

  // Herbouw de uitgaven-paneel-content (sub-bar + actieve sub-content).
  const panel = document.getElementById('p-uitgaven');
  if (panel) panel.innerHTML = buildPanel('uitgaven');

  applyInputModes();
  requestAnimationFrame(drawAllPies);
}

/**
 * Switch tabs by id alone — zoekt het juiste tab-element op via
 * `data-tab-id`. Backwards-compatible met legacy sub-ids: een aanroep met
 * 'vast' / 'variabel' / 'extra' / 'svast' / 'svar' wordt automatisch
 * doorgezet naar switchSubTab() zodat oude inline-onclicks blijven werken.
 */
export function switchTabById(id) {
  const legacySubs = ['vast', 'variabel', 'extra', 'svast', 'svar'];
  if (legacySubs.includes(id)) {
    switchSubTab(id);
    return;
  }
  const el = document.querySelector(`.tab[data-tab-id="${id}"]`)
          || document.querySelector(`.bnav-btn[data-tab-id="${id}"]`);
  if (el) switchTab(id, el);
}

/** Repaint just the modal (gebruikt door dir/rek-toggles, behoudt input-waarden). */
export function renderModal() {
  // Bewaar wat de gebruiker al heeft ingetypt, want innerHTML wist het.
  const amt  = document.getElementById('md-amt')?.value;
  const cat  = document.getElementById('md-cat')?.value;
  const desc = document.getElementById('md-desc')?.value;
  const date = document.getElementById('md-date')?.value;

  // Sync state.modalCat met DOM zodat buildBegrootSection bij rebuild
  // meteen de juiste matches kan tonen (de <select>-value wordt pas NA
  // de innerHTML hieronder terug-gezet).
  if (cat !== undefined) state.modalCat = cat;

  document.getElementById('modal-root').innerHTML = buildAddModal();

  if (amt  !== undefined) { const el = document.getElementById('md-amt');  if (el) el.value = amt; }
  if (cat  !== undefined) { const el = document.getElementById('md-cat');  if (el) el.value = cat; }
  if (desc !== undefined) { const el = document.getElementById('md-desc'); if (el) el.value = desc; }
  if (date !== undefined) { const el = document.getElementById('md-date'); if (el) el.value = date; }
}

/** Dispatch a tab id to the right panel builder. */
function buildPanel(id) {
  if (id === 'home')      return buildHome();
  if (id === 'inkomsten') return buildInkomsten();
  if (id === 'dromen')    return buildDromen();
  if (id === 'overzicht') return buildSamenOverzicht();
  if (id === 'uitgaven')  return buildUitgaven();
  return '';
}

/**
 * The original HTML had the app shell inline. With Vite, `index.html`
 * starts as just `<div id="app"></div>`, so the first render creates
 * the header, tab bar, and panel container. Subsequent renders only
 * rewrite the inner contents.
 */
function ensureShell() {
  const app = document.getElementById('app');
  if (app.querySelector('.header')) return; // already mounted

  app.classList.add('app');
  app.innerHTML = `
    <div class="header">
      <div class="logo">
        <!-- Standaard-mark: groene gradient achtergrond + wit blad. Matcht
             de favicon.svg zodat tab + header dezelfde brand-identiteit
             hebben. id's per gradient zijn uniek om SSR/dubbele-mount-
             collisions te vermijden. -->
        <svg class="logo-mark logo-default" viewBox="0 0 24 24" aria-hidden="true">
          <defs>
            <linearGradient id="florijnLogoBg" x1="0" y1="0" x2="24" y2="24" gradientUnits="userSpaceOnUse">
              <stop offset="0%" stop-color="#00865A"/>
              <stop offset="100%" stop-color="#005F42"/>
            </linearGradient>
          </defs>
          <rect width="24" height="24" rx="5" fill="url(#florijnLogoBg)"/>
          <path d="M18 6 Q 17.2 17.2 6 18 Q 6.8 6.8 18 6 Z" fill="#fff"/>
        </svg>
        <!-- Pride-mark: zes horizontale rainbow-banden vervangen het groen,
             blad blijft wit. Toont alleen wanneer data-theme="pride" actief
             is (zie CSS). De vlag-band-stops gebruiken dubbele-stop-truc om
             scherpe randen tussen kleuren te krijgen i.p.v. een gradient. -->
        <svg class="logo-mark logo-pride" viewBox="0 0 24 24" aria-hidden="true">
          <defs>
            <linearGradient id="florijnLogoBgPride" x1="0" y1="0" x2="0" y2="24" gradientUnits="userSpaceOnUse">
              <stop offset="0%"      stop-color="#e40303"/>
              <stop offset="16.66%"  stop-color="#e40303"/>
              <stop offset="16.66%"  stop-color="#ff8c00"/>
              <stop offset="33.33%"  stop-color="#ff8c00"/>
              <stop offset="33.33%"  stop-color="#ffed00"/>
              <stop offset="50%"     stop-color="#ffed00"/>
              <stop offset="50%"     stop-color="#008026"/>
              <stop offset="66.66%"  stop-color="#008026"/>
              <stop offset="66.66%"  stop-color="#004dff"/>
              <stop offset="83.33%"  stop-color="#004dff"/>
              <stop offset="83.33%"  stop-color="#750787"/>
              <stop offset="100%"    stop-color="#750787"/>
            </linearGradient>
          </defs>
          <rect width="24" height="24" rx="5" fill="url(#florijnLogoBgPride)"/>
          <path d="M18 6 Q 17.2 17.2 6 18 Q 6.8 6.8 18 6 Z" fill="#fff"/>
        </svg>
        <span class="logo-text">Florijn</span>
        <span class="sync-indicator" id="sync-indicator" hidden
              title="Wijzigingen wachten op sync (worden automatisch verstuurd zodra je weer online bent)."></span>
      </div>
      <div class="psw-row"></div>
    </div>
    <div class="tabs" id="tabbar"></div>
    <div id="panels"></div>
    <nav class="bottom-nav" id="bottomnav" aria-label="Hoofdnavigatie"></nav>
    <button class="fab" id="fab" type="button" aria-label="Nieuwe transactie of item toevoegen" onclick="openFab()">
      ${icon('plus', 'color:currentColor', 24)}
    </button>
    <div id="modal-root"></div>
  `;
}
